import { useReactiveVar } from '@apollo/client'
import { CartItemFragment } from '@emico-hooks/cart-fragment'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Plural, Trans } from '@lingui/macro'
import AnimateHeight from 'react-animate-height'
import { Transition, TransitionGroup } from 'react-transition-group'

import { useBreakpoints } from '@emico/ui'

import { CtaButton } from './CtaButton'
import useCartItemRestoration from './useCartItemRestoration'
import { FreeGifts } from '../../gifts/FreeGifts'
import { CurrencyEnum } from '../../graphql/schema.generated'
import FormError from '../../input/FormError'
import { headerSizeVar } from '../../layout/Header/HeaderContainer'
import Separator from '../../layout/Separator'
import Heading from '../../typography/Heading'
import useViewCart from '../../utils/ga4/useViewCart'
import { CustomerCouponVouchers } from '../../vouchers/CustomerCouponVouchers'
import CartItem from '../CartItem'
import EmptyState from '../CartPageEmptyState/EmptyState'
import CartPageOrderSummary from '../CartPageOrderSummary'

const Row = styled.div`
    @media screen and (min-width: 992px) {
        display: flex;

        max-width: 1100px;
        margin: 0 auto 30px;
    }
`

const ItemOverview = styled.div`
    @media screen and (min-width: 992px) {
        margin-bottom: 40px;
        min-width: 0;
        flex: 1 1 auto;
    }
`

const OrderSummary = styled.div`
    position: relative;
    @media screen and (min-width: 768px) {
        margin-top: 2rem;
    }

    @media screen and (min-width: 992px) {
        width: 270px;
        flex: 0 0 auto;
        margin-left: 60px;
    }

    @media screen and (min-width: 1080px) {
        margin-left: 112px;
    }
`

const StickyCartPageOrderSummary = styled(CartPageOrderSummary, {
    shouldForwardProp: (prop) => prop !== 'top',
})<{
    top: number
}>`
    position: sticky;
    top: ${(props) => props.top + 20}px;
    z-index: 2;
`

const headerStyles = css`
    display: flex;
    align-items: center;
    gap: 0.5em;
`

export const StyledHeading = styled(Heading)`
    ${headerStyles}
    margin-bottom: 10px;
`

const StyledSubHeading = styled(Heading)`
    ${headerStyles}
    margin: 20px 0 10px;
`

const StyledItemCount = styled.span`
    font-size: 0.5em;
    text-transform: lowercase;
    font-weight: normal;
`

const section = css`
    margin-bottom: 50px;
`

const CartSection = styled.div`
    ${section}
`

const PersonalShopperSection = styled.section`
    ${section}
`

export const ItemCount = ({ quantity }: { quantity: number }) => (
    <StyledItemCount as="span">
        (
        <Plural
            id="cart.cartItemListing.numberOfItems"
            value={quantity}
            one="# item"
            zero="# items"
            other="# items"
        />
        )
    </StyledItemCount>
)
export const CartPageContent = () => {
    useViewCart()

    const {
        cartItems: allItems,
        loading,
        removeItem,
        restoreItem,
        removeItemFromHistory,
        quantity,
    } = useCartItemRestoration()

    const headerSize = useReactiveVar(headerSizeVar)

    const { isMobile } = useBreakpoints()

    const currency = allItems?.at(0)?.prices?.price.currency ?? CurrencyEnum.EUR

    const unavailableItems = allItems?.filter(
        (item: CartItemFragment) =>
            item.reservation.salableQty < item.quantity &&
            !item.extensionAttributes.isPersonalShopperBoxItem &&
            !item.extensionAttributes.isFreeGift,
    )

    const availableItems = allItems?.filter(
        (item) =>
            item.reservation.salableQty >= item.quantity &&
            !item.extensionAttributes.isPersonalShopperBoxItem &&
            !item.extensionAttributes.isFreeGift,
    )

    const personalShopperItems = allItems?.filter(
        (item) =>
            item.reservation.salableQty >= item.quantity &&
            item.extensionAttributes.isPersonalShopperBoxItem,
    )

    if (
        (!availableItems || !availableItems?.length) &&
        (!personalShopperItems || !personalShopperItems?.length) &&
        !loading
    ) {
        return <EmptyState />
    }

    const freeGiftCount =
        allItems?.filter((item) => item.extensionAttributes.isFreeGift)
            ?.length ?? 0

    const unavailableCount = unavailableItems?.length ?? 0
    const showGeneralError = unavailableCount > 0
    const personalShopperItemCount =
        personalShopperItems?.reduce((acc, item) => acc + item.quantity, 0) || 0
    const regularItemCount = quantity - personalShopperItemCount - freeGiftCount

    // At least one available items has an invalid quantity set

    const showQuantityError = availableItems?.find(
        (item) =>
            item?.quantity >
            (item?.product?.onlyXLeftInStock ?? Number.MAX_SAFE_INTEGER),
    )

    const renderItem = (item: CartItemFragment, disabled: boolean) => (
        <Transition key={item.id} timeout={600}>
            {(state) => {
                const states = {
                    unmounted: 0,
                    entering: 0,
                    entered: 'auto',
                    exiting: 0,
                    exited: 0,
                }

                return (
                    <AnimateHeight
                        height={states[state]}
                        duration={600}
                        animateOpacity
                    >
                        <Separator />
                        <CartItem
                            cartItem={item}
                            currency={currency}
                            remove={removeItem!}
                            restore={restoreItem!}
                            onClose={removeItemFromHistory!}
                            disabled={disabled}
                        />
                    </AnimateHeight>
                )
            }}
        </Transition>
    )

    const items = regularItemCount > 0 && (
        <CartSection>
            <StyledHeading variant="h2" element="h1" color="dark">
                <Trans id="cart.cartItemListing.heading">Your cart</Trans>
                <ItemCount quantity={regularItemCount} />
            </StyledHeading>
            {unavailableCount > 0 && (
                <StyledSubHeading variant="h4" element="h2">
                    <Trans id="cart.cartItemListing.notAvailableHeading">
                        Not available
                    </Trans>
                </StyledSubHeading>
            )}
            <TransitionGroup>
                {unavailableItems?.map((item) => renderItem(item, true))}
                {unavailableCount > 0 && <Separator />}
                {availableItems?.map((item) => renderItem(item, false))}
            </TransitionGroup>
        </CartSection>
    )

    const personalShopper = personalShopperItemCount > 0 && (
        <PersonalShopperSection>
            <StyledHeading variant="h2" element="h2" color="dark">
                <Trans id="cart.cartItemListing.personalShopperHeading">
                    Mijn personal shopper items
                </Trans>
                <ItemCount quantity={personalShopperItemCount} />
            </StyledHeading>
            <TransitionGroup>
                {personalShopperItems?.map((item) => renderItem(item, false))}
            </TransitionGroup>
        </PersonalShopperSection>
    )

    return (
        <>
            {showGeneralError && (
                <Row>
                    <FormError autoHide={false}>
                        <Plural
                            id="cart.cartItemListing.notAvailableGeneralError"
                            value={unavailableCount}
                            one="Item unavailable. Remove the item from your cart."
                            other="Items unavailable. Remove the items from your cart."
                        />
                    </FormError>
                </Row>
            )}
            {showQuantityError && (
                <Row>
                    <FormError autoHide={false}>
                        <Trans id="cart.cartItemListing.quantityGeneralError">
                            One or more items are not available in the selected
                            quantity.
                        </Trans>
                    </FormError>
                </Row>
            )}
            <Row>
                <ItemOverview>
                    {items}
                    {personalShopper}
                    <FreeGifts />
                    {!isMobile && <CustomerCouponVouchers />}
                    {!isMobile && <CtaButton />}
                </ItemOverview>

                <OrderSummary>
                    <StickyCartPageOrderSummary top={headerSize?.height ?? 0} />
                </OrderSummary>
            </Row>
        </>
    )
}
